import { useRef, useState, useEffect, useCallback } from 'react';
import gsap from 'gsap';
import styles from './ContactClapper.module.css';
import FilmStripFooter from './FilmStripFooter.jsx';

/* ─── tiny field sub-component ─── */
function Field({ id, label, type = 'text', value, placeholder, active, onFocus, onBlur, onChange, required }) {
  return (
    <div className={`${styles.field} ${active ? styles.fieldActive : ''}`}>
      <label htmlFor={id} className={styles.label}>{label}</label>
      {type === 'textarea' ? (
        <textarea
          id={id}
          className={`${styles.input} ${styles.textarea}`}
          value={value}
          placeholder={placeholder}
          onFocus={onFocus}
          onBlur={onBlur}
          onChange={onChange}
          rows={3}
          required={required}
        />
      ) : (
        <input
          id={id}
          type={type}
          className={styles.input}
          value={value}
          placeholder={placeholder}
          onFocus={onFocus}
          onBlur={onBlur}
          onChange={onChange}
          required={required}
        />
      )}
      <div className={styles.underline} />
    </div>
  );
}

/* ─── main component ─── */
export default function ContactClapper({ width = 1440, height = 900, isMobile = false }) {
  const [form, setForm]     = useState({ name: '', email: '', subject: '', message: '' });
  const [phase, setPhase]   = useState('idle');   // idle | open | submitting | success
  const [focused, setFocused] = useState(null);

  const zoneRef    = useRef(null);   // mouse-tracking zone
  const clapperRef = useRef(null);   // full clapper (tilt + float)
  const stickRef   = useRef(null);   // hinged top
  const formRef    = useRef(null);   // form (fades out on success)
  const flashRef   = useRef(null);   // full-canvas flash
  const successRef = useRef(null);   // success overlay
  const floatTl    = useRef(null);
  const phaseRef   = useRef('idle');
  phaseRef.current = phase;

  /* Initialise stick angle + idle float */
  useEffect(() => {
    if (!stickRef.current || !clapperRef.current) return;
    gsap.set(stickRef.current, { transformOrigin: 'bottom center', rotateX: -10 });

    floatTl.current = gsap.timeline({ repeat: -1, yoyo: true })
      .to(clapperRef.current, { y: -10, duration: 3.4, ease: 'sine.inOut' });

    return () => floatTl.current?.kill();
  }, []);

  /* ── 3-D hover tilt ── */
  const onMouseMove = useCallback((e) => {
    if (!zoneRef.current || !clapperRef.current) return;
    const r  = zoneRef.current.getBoundingClientRect();
    const dx = ((e.clientX - r.left)  / r.width  - 0.5) * 2;
    const dy = ((e.clientY - r.top)   / r.height - 0.5) * 2;
    gsap.to(clapperRef.current, {
      rotateY:  dx * 8,
      rotateX: -dy * 5,
      duration: 0.85,
      ease: 'power2.out',
      overwrite: 'auto',
    });
  }, []);

  const onMouseLeave = useCallback(() => {
    if (!clapperRef.current) return;
    gsap.to(clapperRef.current, {
      rotateY: 0, rotateX: 0,
      duration: 1.6,
      ease: 'elastic.out(1, 0.5)',
      overwrite: 'auto',
    });
  }, []);

  /* ── Open clapper on first form focus ── */
  const onFormFocus = useCallback(() => {
    if (phaseRef.current !== 'idle') return;
    setPhase('open');
    floatTl.current?.pause();
    gsap.to(clapperRef.current, { y: 0, duration: 0.55, ease: 'power2.out', overwrite: true });
    gsap.to(stickRef.current, {
      rotateX: -64,
      duration: 1.15,
      ease: 'back.out(1.5)',
    });
  }, []);

  /* ── Micro-vibration on typing ── */
  const onInput = useCallback(() => {
    if (!clapperRef.current) return;
    const rx = gsap.utils.random(-1.5, 1.5);
    const ry = gsap.utils.random(-0.8, 0.8);
    gsap.timeline()
      .to(clapperRef.current, { x: rx, y: ry, duration: 0.07 })
      .to(clapperRef.current, { x: 0, y: 0, duration: 0.2, ease: 'power2.out' });
  }, []);

  /* ── Submit: snap → flash → success ── */
  const onSubmit = useCallback((e) => {
    e.preventDefault();
    if (phaseRef.current === 'submitting' || phaseRef.current === 'success') return;
    setPhase('submitting');

    gsap.timeline()
      /* snap stick closed */
      .to(stickRef.current,  { rotateX: 0, duration: 0.16, ease: 'power4.in' })
      /* cinematic flash */
      .to(flashRef.current,  { opacity: 0.92, duration: 0.07 })
      .to(flashRef.current,  { opacity: 0,    duration: 0.45, ease: 'power3.out' })
      /* fade form out */
      .to(formRef.current,   { opacity: 0, duration: 0.35, ease: 'power2.out' }, '-=0.38')
      /* reveal success */
      .call(() => setPhase('success'))
      .fromTo(successRef.current,
        { opacity: 0, y: 14 },
        { opacity: 1, y: 0, duration: 0.75, ease: 'power2.out' }
      )
      /* restart idle float */
      .call(() => {
        if (!clapperRef.current) return;
        floatTl.current = gsap.timeline({ repeat: -1, yoyo: true })
          .to(clapperRef.current, { y: -10, duration: 3.4, ease: 'sine.inOut' });
      }, null, '+=1.2');
  }, []);

  const update = (key) => (e) => {
    setForm(s => ({ ...s, [key]: e.target.value }));
    onInput();
  };

  // ── Mobile layout: clean full-screen contact form ────────────────────────
  if (isMobile) {
    return (
      <div style={{ width: '100%', height: '100%', background: '#020202', overflowY: 'auto', WebkitOverflowScrolling: 'touch', position: 'relative' }}>
        <div style={{ padding: '56px 24px 40px' }}>
          {/* Header */}
          <div className="mono" style={{ color: 'var(--bone-50)', fontSize: 10, display: 'flex', gap: 12, alignItems: 'center', marginBottom: 8 }}>
            <span style={{ color: 'var(--red)' }}>08</span>
            <span style={{ width: 28, height: 1, background: 'var(--bone-30)', display: 'inline-block' }} />
            GET IN TOUCH
          </div>

          {/* Form — no ref needed on mobile; visibility is state-driven */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (phase === 'submitting' || phase === 'success') return;
              setPhase('success');
            }}
            style={{ display: phase === 'success' ? 'none' : 'flex', flexDirection: 'column', gap: 20 }}
          >
            <Field
              id="m-name" label="NAME" type="text"
              value={form.name} placeholder="Your name"
              active={focused === 'name'}
              onFocus={() => setFocused('name')} onBlur={() => setFocused(null)}
              onChange={update('name')}
            />
            <Field
              id="m-email" label="EMAIL" type="email"
              value={form.email} placeholder="your@email.com"
              active={focused === 'email'}
              onFocus={() => setFocused('email')} onBlur={() => setFocused(null)}
              onChange={update('email')} required
            />
            <Field
              id="m-sub" label="SUBJECT" type="text"
              value={form.subject} placeholder="What's the shoot?"
              active={focused === 'subject'}
              onFocus={() => setFocused('subject')} onBlur={() => setFocused(null)}
              onChange={update('subject')}
            />
            <Field
              id="m-msg" label="MESSAGE" type="textarea"
              value={form.message} placeholder="Tell me about your project..."
              active={focused === 'message'}
              onFocus={() => setFocused('message')} onBlur={() => setFocused(null)}
              onChange={update('message')}
            />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
              <button
                type="submit"
                disabled={phase === 'submitting'}
                style={{
                  fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.18em',
                  textTransform: 'uppercase', color: '#fff',
                  background: '#ff2a1f', border: 'none',
                  borderRadius: 2, padding: '12px 24px',
                  cursor: phase === 'submitting' ? 'not-allowed' : 'pointer',
                  opacity: phase === 'submitting' ? 0.6 : 1,
                  boxShadow: '0 2px 12px rgba(255,42,31,0.40)',
                  marginLeft: 'auto',
                }}
              >
                ACTION —
              </button>
            </div>
          </form>

          {/* Success — state-driven, no ref needed on mobile */}
          <div
            style={{
              display: phase === 'success' ? 'block' : 'none',
              marginTop: 32,
            }}
            aria-live="polite"
          >
            <div style={{ fontFamily: 'var(--font-serif)', fontSize: 26, color: 'var(--bone)', lineHeight: 1.2, marginBottom: 8 }}>
              Message received —
            </div>
            <div style={{ fontFamily: 'var(--font-serif)', fontStyle: 'italic', fontSize: 22, color: 'var(--red)', lineHeight: 1.2 }}>
              let's create something.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', width, height, background: '#020202', overflow: 'hidden' }}>

      {/* Bg layers */}
      <div className={styles.bgGradient} />
      <div className={styles.vignette}   />
      <div className={styles.spotlight}  />

      {/* Full-canvas flash */}
      <div ref={flashRef} className={styles.flash} aria-hidden="true" />

      {/* Header */}
      <div className={styles.header}>
        <div className={`mono ${styles.headerLeft}`}>
          <span style={{ color: 'var(--red)' }}>06</span>
          <span className={styles.headerRule} />
          GET IN TOUCH
        </div>
      </div>

      {/* Mouse-tracking zone + perspective host */}
      <div
        ref={zoneRef}
        className={styles.zone}
        style={{ perspective: 1100 }}
        onMouseMove={onMouseMove}
        onMouseLeave={onMouseLeave}
      >
        {/* ═══════════════════════ CLAPPERBOARD ═══════════════════════ */}
        <div ref={clapperRef} className={styles.clapper}>

          {/* ── Hinged stick (top) ── */}
          <div ref={stickRef} className={styles.stick} aria-hidden="true">
            {/* Diagonal stripes via CSS repeating-gradient — no gaps, perfectly uniform */}
            <div className={styles.stripes} aria-hidden="true" />
            <div className={styles.stickMeta}>
              <span className={styles.stickTitle}>
                <span className={styles.stickTitleDot} />
                Get In Touch
              </span>
            </div>
            {/* Hinge detail */}
            <div className={styles.hinge}>
              <div className={styles.hingeRivet} />
              <div className={styles.hingeRivet} />
              <div className={styles.hingeRivet} />
            </div>
          </div>

          {/* ── Main board (body) ── */}
          <div className={styles.body}>

            {/* Film metadata strip */}
            <div className={styles.filmStrip}>
              {[
                ['PRODUCTION', 'DREW HALLÉ'],
                ['SCENE', '08'],
                ['TAKE', '01'],
                ['ROLL', 'A'],
              ].map(([k, v]) => (
                <div key={k} className={styles.filmCell}>
                  <span className={styles.filmKey}>{k}</span>
                  <span className={styles.filmVal}>{v}</span>
                </div>
              ))}
            </div>

            <div className={styles.divider} />

            {/* Contact form */}
            <form
              ref={formRef}
              className={styles.form}
              onSubmit={onSubmit}
              onFocus={onFormFocus}
            >
              <div className={styles.row}>
                <Field
                  id="cc-name"  label="NAME"  type="text"
                  value={form.name}   placeholder="Your name"
                  active={focused === 'name'}
                  onFocus={() => setFocused('name')}   onBlur={() => setFocused(null)}
                  onChange={update('name')}
                />
                <Field
                  id="cc-email" label="EMAIL" type="email"
                  value={form.email}  placeholder="your@email.com"
                  active={focused === 'email'}
                  onFocus={() => setFocused('email')}  onBlur={() => setFocused(null)}
                  onChange={update('email')} required
                />
              </div>

              <Field
                id="cc-sub"  label="SUBJECT"  type="text"
                value={form.subject} placeholder="What's the shoot?"
                active={focused === 'subject'}
                onFocus={() => setFocused('subject')} onBlur={() => setFocused(null)}
                onChange={update('subject')}
              />

              <Field
                id="cc-msg"  label="MESSAGE"  type="textarea"
                value={form.message} placeholder="Tell me about your project..."
                active={focused === 'message'}
                onFocus={() => setFocused('message')} onBlur={() => setFocused(null)}
                onChange={update('message')}
              />

              <div className={styles.formFooter}>
                <button
                  type="submit"
                  className={styles.cta}
                  disabled={phase === 'submitting'}
                >
                  <span className={styles.ctaDot} />
                  ACTION —
                </button>
              </div>
            </form>

            {/* Success overlay (fades in after submit) */}
            <div ref={successRef} className={styles.success} aria-live="polite">
              <div className={styles.successLine1}>Message received —</div>
              <div className={styles.successLine2}>let's create something.</div>
            </div>

          </div>
          {/* ── end body ── */}

        </div>
        {/* ── end clapper ── */}
      </div>

      {/* ── Full-bleed film strip footer ── */}
      <FilmStripFooter width={width} />

    </div>
  );
}
